// Sélecteur 1 set / 2 achetés + 1 offert (Ongles Lab, 06/10/2026).
// En mode 2+1 : intercepte l'ajout au panier du formulaire produit et ajoute les 3 sets choisis ;
// la remise automatique « 2 + 1 offert » (collection kits-faux-ongles) fait le reste.
import { CartUpdatedEvent } from '@theme/events';

const eur = (c) => (c / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
let dataPromise = null;
// Libellé de forme comme sur le site : Extra-court, Carré court, Amande court, Amande, Coffin
const shape = (x) => (/^extra/i.test(x.f || '') ? 'Extra-court' : x.l === 'Court' ? `${x.f} court` : x.f || '');

class OlBundle extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.current = JSON.parse(this.dataset.current);
    // À l'arrivée : seul le set 1 est rempli (le set de la page), 2 et 3 sont à choisir
    this.slots = [this.current, null, null];
    this.slots.forEach((_, i) => this.renderSlot(i));
    this.activeSlot = 0;
    this.dialog = this.querySelector('[data-ref="dialog"]');
    this.grid = this.querySelector('[data-ref="grid"]');
    this.form = this.closest('product-form')?.querySelector('form');

    this.querySelectorAll('input[type="radio"]').forEach((r) => r.addEventListener('change', () => this.setMode(r.value)));
    this.querySelectorAll('[data-slot]').forEach((b) =>
      b.addEventListener('click', () => this.openPicker(parseInt(b.dataset.slot, 10)))
    );
    // Petite croix : vide la case, on peut recliquer pour choisir un autre modèle
    this.querySelectorAll('[data-remove]').forEach((x) => {
      const rm = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const i = parseInt(x.closest('[data-slot]').dataset.slot, 10);
        this.slots[i] = null;
        this.renderSlot(i);
        this.updatePrices();
      };
      x.addEventListener('click', rm);
      x.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') rm(e); });
    });
    this.querySelector('[data-ref="close"]').addEventListener('click', () => this.dialog.close());
    this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.dialog.close(); });
    this.querySelectorAll('[data-filter]').forEach((s) => s.addEventListener('change', () => this.renderGrid()));

    // Avant le gestionnaire du thème (phase de capture sur la cible)
    this.onSubmit = this.onSubmit.bind(this);
    this.form?.addEventListener('submit', this.onSubmit, true);

    const def = this.dataset.defaultOption === '1' ? '1' : '3';
    const radio = this.querySelector(`input[value="${def}"]`);
    if (radio) radio.checked = true;
    this.setMode(def);
    // Le bouton d'ajout au panier est rendu après ce bloc
    requestAnimationFrame(() => this.updatePrices());
    const idle = window.requestIdleCallback || ((f) => setTimeout(f, 1200));
    idle(() => this.preload(), { timeout: 3000 });
    this.querySelectorAll('[data-slot]').forEach((b) => b.addEventListener('pointerenter', () => this.load(), { once: true }));
    window.addEventListener('load', () => this.updatePrices(), { once: true });
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.onSubmit, true);
  }

  setMode(mode) {
    this.mode = mode;
    this.classList.toggle('is-offer', mode === '3');
    this.querySelectorAll('[data-opt]').forEach((o) => o.classList.toggle('is-selected', o.dataset.opt === mode));
    this.updatePrices();
  }

  isComplete() {
    return this.slots.every(Boolean);
  }

  renderSlot(i) {
    const s = this.slots[i];
    const b = this.querySelector(`[data-slot="${i}"]`);
    if (!b) return;
    b.classList.toggle('is-empty', !s);
    if (!s) return;
    b.querySelector('.ol-bundle__slot-img').src = s.img;
    b.querySelector('.ol-bundle__slot-name').textContent = s.t;
    b.querySelector('.ol-bundle__slot-meta').textContent = shape(s);
  }

  totals() {
    // Case vide : on compte le prix du set de la page pour l'affichage
    const p = this.slots.map((s) => (s ? s.p : this.current.p));
    const all = p.reduce((a, b) => a + b, 0);
    const total = all - Math.min(...p);
    return { all, total, save: all - total };
  }

  updatePrices() {
    const { all, total, save } = this.totals();
    const set = (ref, v) => { const el = this.querySelector(`[data-ref="${ref}"]`); if (el) el.textContent = v; };
    set('total', eur(total));
    set('was', eur(all));
    set('per', `soit ${eur(Math.round(total / 3))} / set`);
    set('save', `Économise ${eur(save)}`);
    // Prix affiché dans le bouton d'ajout au panier
    const btn = this.closest('product-form')?.querySelector('[data-ref="add-to-cart-button-container"]');
    if (!btn) return;
    const price = btn.querySelector('[data-ref="price"]');
    const was = btn.querySelector('[data-ref="compare-at-price"]');
    const label = btn.querySelector('.full-add-to-cart-text');
    const addBtn = btn.querySelector('[data-ref="add-to-cart-button"]');
    if (label && !label.dataset.olLabel) label.dataset.olLabel = label.textContent.trim();
    const incomplete = this.mode === '3' && !this.isComplete();
    this.classList.toggle('is-incomplete', incomplete);
    if (addBtn) addBtn.disabled = false;
    const missing = this.slots.filter((s) => !s).length;
    const msg = missing === 1 ? 'Choisis ton dernier modèle' : `Choisis tes ${missing} autres modèles`;
    const need = this.querySelector('[data-ref="need"]');
    if (need) need.textContent = missing === 1 ? 'Plus qu’un modèle à choisir pour profiter du 3e offert.' : `Choisis tes ${missing} autres modèles pour profiter du 3e offert.`;
    btn.classList.toggle('ol-bundle-incomplete', incomplete);
    if (label) label.textContent = incomplete ? msg : label.dataset.olLabel;
    if (this.mode === '3') {
      if (price) price.textContent = eur(total);
      if (was) was.textContent = eur(all);
    } else {
      if (price) price.textContent = eur(this.current.p);
      if (was) was.textContent = '';
    }
  }

  // Données des sets : cache de session (30 min) + préchargement à l'inactivité de la page
  async load() {
    if (!dataPromise) {
      const KEY = 'ol-bundle-data-v2';
      try {
        const c = JSON.parse(sessionStorage.getItem(KEY) || 'null');
        if (c && Date.now() - c.t < 30 * 60 * 1000 && Array.isArray(c.d)) dataPromise = Promise.resolve(c.d);
      } catch (e) {}
      if (!dataPromise) {
        dataPromise = fetch(this.dataset.dataUrl, { credentials: 'same-origin' })
          .then((r) => r.text())
          .then((t) => {
            const d = JSON.parse(t.trim());
            try { sessionStorage.setItem(KEY, JSON.stringify({ t: Date.now(), d })); } catch (e) {}
            return d;
          })
          .catch(() => { dataPromise = null; return []; });
      }
    }
    return dataPromise;
  }

  preload() {
    this.load().then((d) => {
      // Précharge les premières vignettes pour une ouverture immédiate
      d.slice(0, 12).forEach((x) => { const i = new Image(); i.decoding = 'async'; i.src = x.img; });
    });
  }

  async openPicker(i) {
    this.activeSlot = i;
    this.querySelector('[data-ref="slotnum"]').textContent = i === 2 ? '3 (offert)' : String(i + 1);
    this.dialog.showModal();
    // Emma 06/10 : Safari entourait la croix en bleu (focus auto sur le 1er bouton) → on place le focus sur la fenêtre elle-même
    const sheet = this.dialog.querySelector('.ol-bundle__sheet');
    if (sheet) { sheet.tabIndex = -1; sheet.focus({ preventScroll: true }); }
    this.items = await this.load();
    if (!this.items.some((x) => x.id === this.current.id)) this.items.unshift(this.current);
    this.fillFilters();
    this.renderGrid();
  }

  fillFilters() {
    if (this._filtersDone) return;
    this._filtersDone = true;
    const order = { f: ['Extra-Courte', 'Carré', 'Amande', 'Coffin'], l: ['Extra-Court', 'Court', 'Long'] };
    this.querySelectorAll('[data-filter]').forEach((sel) => {
      const k = sel.dataset.filter;
      let vals = [...new Set(this.items.map((x) => x[k]).filter(Boolean))];
      vals.sort((a, b) => (order[k] ? order[k].indexOf(a) - order[k].indexOf(b) : a.localeCompare(b, 'fr')));
      vals.forEach((v) => sel.insertAdjacentHTML('beforeend', `<option value="${v}">${v}</option>`));
    });
  }

  renderGrid() {
    if (!this.items) return;
    const f = {};
    this.querySelectorAll('[data-filter]').forEach((s) => (f[s.dataset.filter] = s.value));
    const list = this.items.filter((x) => (!f.c || x.c === f.c) && (!f.f || x.f === f.f) && (!f.l || x.l === f.l));
    const sel = this.slots[this.activeSlot]?.id;
    this.grid.innerHTML = list.length
      ? list
          .map(
            (x) => `<button type="button" class="ol-bundle__card${x.id === sel ? ' is-selected' : ''}" data-id="${x.id}">
              <img src="${x.img}" alt="" loading="lazy" decoding="async" width="300" height="300">
              <span class="ol-bundle__card-name">${x.t}</span>
              <span class="ol-bundle__card-meta">${shape(x)}</span>
            </button>`
          )
          .join('')
      : '<p class="ol-bundle__loading">Aucun modèle avec ces filtres.</p>';
    this.grid.querySelectorAll('[data-id]').forEach((b) =>
      b.addEventListener('click', () => this.choose(this.items.find((x) => String(x.id) === b.dataset.id)))
    );
  }

  choose(item) {
    if (!item) return;
    this.slots[this.activeSlot] = item;
    this.renderSlot(this.activeSlot);
    this.dialog.close();
    this.updatePrices();
  }

  async onSubmit(event) {
    if (this.mode !== '3') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!this.isComplete()) {
      this.openPicker(this.slots.findIndex((s) => !s));
      return;
    }
    const btn = this.closest('product-form')?.querySelector('[data-ref="add-to-cart-button"]');
    btn?.classList.add('is-loading');
    const byId = new Map();
    this.slots.forEach((s) => byId.set(s.id, (byId.get(s.id) || 0) + 1));
    const items = [...byId].map(([id, quantity]) => ({ id, quantity }));
    const sections = ['auto-gifts'];
    const drawer = document.querySelector('cart-drawer');
    if (drawer?.dataset.sectionId) sections.push(drawer.dataset.sectionId);
    try {
      const res = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sections.join(',') }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'Erreur');
      const count = document.querySelector('cart-icon')?.getCurrentItemCount?.();
      document.dispatchEvent(
        new CartUpdatedEvent({}, { itemCount: count != null ? count + 3 : undefined, source: 'product-form', sections: data.sections })
      );
      setTimeout(() => {
        if (drawer && !drawer.hasAttribute('open') && !drawer.classList.contains('is-open')) document.dispatchEvent(new Event('cart:open'));
      }, 150);
    } catch (e) {
      document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message: e.message } }));
    } finally {
      btn?.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('ol-bundle')) customElements.define('ol-bundle', OlBundle);
